import {
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from '@sitewise/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Mail, Phone, Plus, Trash2, UserCog } from 'lucide-react';
import { useId, useState, type FormEvent } from 'react';

import {
  createContact,
  deleteContact,
  type ContactRow,
} from '../../../api/buildings.js';

interface Props {
  buildingId: string;
  contacts: ContactRow[];
  isLoading?: boolean;
}

export function BuildingContactsCard({ buildingId, contacts, isLoading }: Props) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);

  const deleteMutation = useMutation({
    mutationFn: (contactId: string) => deleteContact(contactId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['building', buildingId, 'contacts'] });
    },
  });

  return (
    <Card>
      <CardContent className="space-y-4 p-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold">On-site contacts</h3>
            <p className="text-xs text-fg-muted">{contacts.length} active</p>
          </div>
          <Button size="sm" onClick={() => setOpen(true)}>
            <Plus className="h-4 w-4" /> Add contact
          </Button>
        </div>

        {isLoading ? (
          <p className="text-sm text-fg-muted">Loading…</p>
        ) : contacts.length === 0 ? (
          <p className="text-sm text-fg-secondary">
            No contacts yet. Add the super, owner rep, or on-call so the team can reach them from
            this page.
          </p>
        ) : (
          <ul className="divide-y divide-border">
            {contacts.map((c) => (
              <li key={c.id} className="flex items-center justify-between gap-3 py-3">
                <div className="flex items-center gap-3">
                  <UserCog className="h-5 w-5 text-fg-muted" />
                  <div>
                    <div className="text-sm font-medium">{c.name}</div>
                    <div className="text-xs text-fg-muted">{c.roleLabel}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 text-xs text-fg-secondary">
                  {c.phone ? (
                    <a href={`tel:${c.phone}`} className="inline-flex items-center gap-1 hover:text-fg">
                      <Phone className="h-3.5 w-3.5" /> {c.phone}
                    </a>
                  ) : null}
                  {c.email ? (
                    <a
                      href={`mailto:${c.email}`}
                      className="inline-flex items-center gap-1 hover:text-fg"
                    >
                      <Mail className="h-3.5 w-3.5" /> {c.email}
                    </a>
                  ) : null}
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label="Remove contact"
                    onClick={() => {
                      if (confirm(`Remove ${c.name} from this building?`)) {
                        deleteMutation.mutate(c.id);
                      }
                    }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
      <AddContactDialog open={open} onOpenChange={setOpen} buildingId={buildingId} />
    </Card>
  );
}

function AddContactDialog({
  open,
  onOpenChange,
  buildingId,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  buildingId: string;
}) {
  const queryClient = useQueryClient();
  const nameId = useId();
  const roleId = useId();
  const phoneId = useId();
  const emailId = useId();
  const [name, setName] = useState('');
  const [role, setRole] = useState('Super');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () =>
      createContact(buildingId, {
        name,
        roleLabel: role,
        phone: phone || null,
        email: email || null,
        notes: null,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['building', buildingId, 'contacts'] });
      setName('');
      setRole('Super');
      setPhone('');
      setEmail('');
      onOpenChange(false);
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'Failed to add contact'),
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!name.trim() || !role.trim()) {
      setError('Name and role are required');
      return;
    }
    mutation.mutate();
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add a contact</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor={nameId}>Name</Label>
              <Input id={nameId} value={name} onChange={(e) => setName(e.target.value)} autoFocus />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor={roleId}>Role</Label>
              <Input
                id={roleId}
                value={role}
                onChange={(e) => setRole(e.target.value)}
                placeholder="Super"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor={phoneId}>Phone</Label>
              <Input
                id={phoneId}
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                type="tel"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor={emailId}>Email</Label>
              <Input
                id={emailId}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                type="email"
              />
            </div>
          </div>
          {error ? <p className="text-sm text-danger">{error}</p> : null}
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? 'Adding…' : 'Add contact'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
